import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { contactService, ContactInfo, defaultContactInfo } from '../services/contactService';

interface ContactContextType {
  contactInfo: ContactInfo;
  loading: boolean;
  updateContactInfo: (info: ContactInfo) => Promise<void>;
}

const ContactContext = createContext<ContactContextType | undefined>(undefined);

export const useContact = (): ContactContextType => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContact must be used within a ContactProvider');
  }
  return context;
};

interface ContactProviderProps {
  children: ReactNode;
}

export const ContactProvider: React.FC<ContactProviderProps> = ({ children }) => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>(defaultContactInfo);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const info = await contactService.getContactInfo();
        setContactInfo(info);
      } catch (error) {
        console.error('Error fetching contact information:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const updateContactInfo = async (info: ContactInfo) => {
    try {
      await contactService.updateContactInfo(info);
      setContactInfo(info);
    } catch (error) {
      console.error('Error updating contact information:', error);
      throw error;
    }
  };

  const value = {
    contactInfo,
    loading,
    updateContactInfo,
  };

  return (
    <ContactContext.Provider value={value}>
      {children}
    </ContactContext.Provider>
  );
};

export default ContactContext; 